import { useCallback, useMemo } from 'react';
import { useQueryClient } from '@tanstack/react-query';

import {
  useGiveNotice,
  useSetSettlementDate,
  useSettleTrade,
  useSettleCash,
  useSettleSecurities,
  useTerminateTrade,
  usePartialRecall,
  useProcessTradeDate,
  useHolidays,
} from '@/hooks/trades';
import { queryKeys } from '@/services/query-keys';
import type { HolidayItem } from '@/types/trades.types';

import {
  getValidActions,
  canPerform as canPerformPredicate,
  type ActionId,
  type TradeActionFields,
} from './trade-actions';

/**
 * Shared lifecycle-action hook (ACTN-04 anti-drift). The single place that
 * BOTH the detail-page action bar and the TradesPage row/bulk surface consume,
 * so the two action surfaces cannot diverge. It:
 *
 *  - COMPOSES the six mutation hooks (give_notice / set_settlement_date /
 *    settle / settle_cash / settle_securities / terminate) plus `useHolidays`,
 *    each as a single TanStack Query subscription. The detail-page modals
 *    (Notice / SettlementDate / Terminate) receive their mutation handle by
 *    prop from the page-level useTradeActions and do NOT re-subscribe (LR-208
 *    r3 M4); the bar's settle/settleCash/settleSecurities ConfirmDialogs are
 *    likewise wired off this single instance.
 *  - exposes the pure `getValidActions` / `canPerform` predicate bound to the
 *    passed trade (the single source of truth for render AND submit, ACTN-02).
 *  - layers DETAIL-key invalidation on top of the underlying hooks' list
 *    invalidation, so a single-trade view refetches and re-gates after any
 *    action (ACTN-03). The underlying hooks already invalidate `tradesKeys.all`
 *    (the list); this adds `queryKeys.trades.detail(id)` without mutating them.
 *
 * @param trade  the freshly-loaded trade (PositionDetail or TradeItem — both
 *   carry the structural `TradeActionFields`). May be null/undefined while the
 *   detail query is loading; in that case no actions are valid.
 * @param enableHolidays  gate the holiday fetch (true only while a
 *   settlement-date dialog is open) — bulk-fetch, cached 1h, filtered locally.
 */
export function useTradeActions(
  trade?: TradeActionFields | null,
  enableHolidays = false,
) {
  const queryClient = useQueryClient();

  const giveNotice = useGiveNotice();
  const setSettlementDate = useSetSettlementDate();
  const settle = useSettleTrade();
  const settleCash = useSettleCash();
  const settleSecurities = useSettleSecurities();
  const terminate = useTerminateTrade();
  const partialRecall = usePartialRecall();
  // LR-544: manual "Open now" for a pending position (fires the Trade Date
  // action). Invalidates the ['positions'] prefix so the detail view refetches.
  const processTradeDate = useProcessTradeDate();

  const holidaysQuery = useHolidays(enableHolidays);
  const holidays = holidaysQuery.data;

  // Pure predicate bound to the current trade. Memoized on the exact fields the
  // predicate reads so the action set is referentially stable across unrelated
  // re-renders (and recomputes the instant the trade refetches and re-gates).
  // Every field `TradeActionFields` declares must be listed here — the literal
  // is a hand-copy of the predicate's input, and a field the predicate reads but
  // this omits is silently `undefined`, which hides the action on the detail
  // page while the blotter (which passes the whole TradeItem) still shows it.
  const {
    status, term_type, notice_served, notice_period_days, maturity_date, delivery_type,
    cash_settled, securities_settled, trade_type, remaining_quantity,
    collateral_type, rate_entry_mode, cash_pool,
  } = trade ?? {};
  const validActions = useMemo<Set<ActionId>>(
    () =>
      status
        ? getValidActions({
            status,
            term_type,
            notice_served,
            // LR-678: give_notice needs the period the maturity date is derived from.
            notice_period_days,
            // LR-695: set_settlement_date on an active trade needs an end date.
            maturity_date,
            delivery_type,
            cash_settled,
            securities_settled,
            // LR-388: partial_recall validity needs the trade side + markable shape left.
            trade_type,
            remaining_quantity,
            collateral_type,
            rate_entry_mode,
            cash_pool,
          })
        : new Set<ActionId>(),
    [
      status, term_type, notice_served, notice_period_days, maturity_date, delivery_type,
      cash_settled, securities_settled, trade_type, remaining_quantity,
      collateral_type, rate_entry_mode, cash_pool,
    ],
  );

  const canPerform = useCallback(
    (actionId: ActionId): boolean => (trade ? canPerformPredicate(trade, actionId) : false),
    [trade],
  );

  // Local holiday lookup (no per-keystroke API): callers pass an ISO date and
  // get the matching active holiday or undefined.
  const holidayFor = useCallback(
    (date: string): HolidayItem | undefined =>
      holidays?.find((h) => h.is_active && h.date === date),
    [holidays],
  );

  // Detail-key invalidation wrapper (ACTN-03). Wrap a per-action onSuccess so it
  // ALSO invalidates the detail query for the acted-on id. The underlying hooks
  // keep their own list invalidation untouched; this layers the detail refetch
  // so the single-trade view re-gates. Returns an onSuccess handler to hand to
  // `mutate(vars, { onSuccess })`.
  const withDetailRefetch = useCallback(
    (id: number, onSuccess?: () => void) => () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.trades.detail(id) });
      onSuccess?.();
    },
    [queryClient],
  );

  return {
    // Per-action mutation handles ({ mutate, isPending, error, reset, ... }).
    giveNotice,
    setSettlementDate,
    settle,
    settleCash,
    settleSecurities,
    terminate,
    partialRecall,
    processTradeDate,

    // Predicate surface (single source of truth for render + submit).
    validActions,
    canPerform,

    // Holiday wiring for the settlement-date picker.
    holidays,
    holidaysQuery,
    holidayFor,

    // Detail-key invalidation helper (callers pass this as the mutation's
    // onSuccess so the single-trade view refetches + re-gates).
    withDetailRefetch,
  };
}
